"use client";

import { Suspense, useState, useRef, useCallback, useEffect } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslations } from "next-intl";
import { AlanaLogo } from "@/components/ui/AlanaLogo";

// ---------------------------------------------------------------------------
// State machine types
// ---------------------------------------------------------------------------

type FlowState =
  | { step: "idle" }
  | { step: "checking_hrd" }
  | { step: "sending" }
  | { step: "code_sent"; email: string }
  | { step: "verifying"; email: string }
  | { step: "error"; email?: string; message: string };

// ---------------------------------------------------------------------------
// Page shell (keeps Suspense wrapper intact)
// ---------------------------------------------------------------------------

export default function LoginPage() {
  return (
    <Suspense>
      <LoginContent />
    </Suspense>
  );
}

// ---------------------------------------------------------------------------
// Main login content
// ---------------------------------------------------------------------------

function LoginContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const t = useTranslations("auth.loginPage");
  const tAuth = useTranslations("auth");

  // ---------------------------------------------------------------------------
  // Error messages — keep in sync with error codes from API routes and /auth/callback
  // ---------------------------------------------------------------------------

  const ERROR_MESSAGES: Record<string, string> = {
    personal_email: t("errors.personalEmail"),
    server_error: t("errors.serverError"),
    session_error: t("errors.sessionError"),
  };

  const errorParam = searchParams.get("error");
  const errorMessage = errorParam ? ERROR_MESSAGES[errorParam] : null;

  const [email, setEmail] = useState("");
  const [flow, setFlow] = useState<FlowState>({ step: "idle" });
  const [resendCooldown, setResendCooldown] = useState(0);

  // Cooldown timer for resend button
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  // ---------------------------------------------------------------------------
  // Social login handlers (go directly to provider, no Auth0 Universal Login)
  // ---------------------------------------------------------------------------

  function handleGoogle() {
    window.location.assign("/auth/login?connection=google-workspace");
  }

  function handleMicrosoft() {
    window.location.assign("/auth/login?connection=azure-ad-b2b");
  }

  // ---------------------------------------------------------------------------
  // Email flow — step 1: HRD check then send OTP (or redirect to SSO)
  // ---------------------------------------------------------------------------

  async function handleEmailSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    if (flow.step === "checking_hrd" || flow.step === "sending") return;

    const trimmedEmail = email.trim();

    // Step 1: Home Realm Discovery — find out which connection to use
    setFlow({ step: "checking_hrd" });

    try {
      const hrdRes = await fetch("/api/auth/hrd", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmedEmail }),
      });

      if (hrdRes.ok) {
        const hrdData = await hrdRes.json();

        if (hrdData.type === "enterprise" && hrdData.connection) {
          // Enterprise SSO detected — redirect to that connection directly.
          // Pass login_hint so the IdP can pre-fill the email field.
          const params = new URLSearchParams({
            connection: hrdData.connection,
            login_hint: trimmedEmail,
          });
          if (hrdData.org_id) {
            params.set("organization", hrdData.org_id);
          }
          window.location.assign(`/auth/login?${params.toString()}`);
          return;
        }
        // type: "passwordless" or "unknown" — fall through to OTP
      }
      // HRD API error or unknown type — fall through to OTP gracefully
    } catch {
      // HRD call failed — degrade gracefully to OTP
    }

    // Step 2: Send OTP (passwordless)
    setFlow({ step: "sending" });

    try {
      const res = await fetch("/api/auth/passwordless/start", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmedEmail }),
      });

      const data = await res.json();

      if (!res.ok) {
        setFlow({
          step: "error",
          message:
            (typeof data?.message === "string" ? data.message : null) ??
            t("errors.failedToSendCode"),
        });
        return;
      }

      setFlow({ step: "code_sent", email: trimmedEmail });
      setResendCooldown(60);
    } catch {
      setFlow({
        step: "error",
        message: t("errors.somethingWentWrong"),
      });
    }
  }

  // ---------------------------------------------------------------------------
  // Email flow — step 2: verify OTP
  // ---------------------------------------------------------------------------

  const handleVerify = useCallback(
    async (code: string, currentEmail: string) => {
      if (flow.step === "verifying") return;
      setFlow({ step: "verifying", email: currentEmail });

      try {
        const res = await fetch("/api/auth/passwordless/verify", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: currentEmail, code }),
        });

        const data = await res.json();

        if (!res.ok) {
          setFlow({
            step: "error",
            email: currentEmail,
            message:
              (typeof data?.message === "string" ? data.message : null) ??
              t("errors.verificationFailed"),
          });
          return;
        }

        // Success — redirect to the URL returned by the API
        router.push(data.redirectTo ?? "/");
      } catch {
        setFlow({
          step: "error",
          email: currentEmail,
          message: t("errors.somethingWentWrong"),
        });
      }
    },
    [flow.step, router, t],
  );

  // ---------------------------------------------------------------------------
  // Resend code
  // ---------------------------------------------------------------------------

  async function handleResend() {
    if (resendCooldown > 0) return;
    const currentEmail =
      flow.step === "code_sent" ||
      flow.step === "verifying" ||
      flow.step === "error"
        ? flow.email
        : email;
    if (!currentEmail) return;

    try {
      const res = await fetch("/api/auth/passwordless/start", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: currentEmail }),
      });
      if (res.ok) {
        setFlow({ step: "code_sent", email: currentEmail });
        setResendCooldown(60);
      } else {
        setFlow({
          step: "error",
          email: currentEmail,
          message: t("errors.failedToResendCode"),
        });
      }
    } catch {
      setFlow({
        step: "error",
        email: currentEmail,
        message: t("errors.failedToResendCode"),
      });
    }
  }

  // ---------------------------------------------------------------------------
  // Go back to email form
  // ---------------------------------------------------------------------------

  function handleBack() {
    setFlow({ step: "idle" });
  }

  // ---------------------------------------------------------------------------
  // Derived state
  // ---------------------------------------------------------------------------

  const isCodeStep =
    flow.step === "code_sent" ||
    flow.step === "verifying" ||
    (flow.step === "error" && flow.email);

  const stepEmail =
    flow.step === "code_sent" ||
    flow.step === "verifying" ||
    (flow.step === "error" && flow.email)
      ? (flow as { email: string }).email
      : email;

  // The continue button shows a loading label during HRD check or OTP send
  const isBusy = flow.step === "checking_hrd" || flow.step === "sending";

  function getContinueLabel() {
    if (flow.step === "checking_hrd") return t("checking");
    if (flow.step === "sending") return t("sendingCode");
    return t("continue");
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <>
      {/* Mobile-only logo */}
      <div className="flex justify-center mb-8 lg:hidden">
        <AlanaLogo variant="icon" className="w-12 h-12" />
      </div>

      <AnimatePresence mode="wait">
        {!isCodeStep ? (
          /* ---------------------------------------------------------------- */
          /* Email form step — identifier-first layout                        */
          /* ---------------------------------------------------------------- */
          <motion.div
            key="email-step"
            initial={{ opacity: 0, x: -16 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 16 }}
            transition={{ duration: 0.2 }}
          >
            <div className="space-y-2 mb-8">
              <h1
                className="text-3xl font-bold"
                style={{ fontFamily: "MuseoModerno, sans-serif" }}
              >
                {t("welcomeBack")}
              </h1>
              <p
                className="text-sm"
                style={{ color: "var(--foreground-muted)" }}
              >
                {t("signInToAccount")}
              </p>
            </div>

            {/* URL error message (e.g., ?error=personal_email) */}
            <AnimatePresence>
              {errorMessage && (
                <motion.div
                  key="url-error"
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.2 }}
                  className="mb-6 px-4 py-3 rounded-xl text-sm"
                  style={{
                    background: "rgba(239,68,68,0.08)",
                    border: "1px solid rgba(239,68,68,0.25)",
                    color: "var(--error, #ef4444)",
                  }}
                >
                  {errorMessage}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Flow error (after a failed start) */}
            <AnimatePresence>
              {flow.step === "error" && !flow.email && (
                <motion.div
                  key="flow-error"
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.2 }}
                  className="mb-6 px-4 py-3 rounded-xl text-sm"
                  style={{
                    background: "rgba(239,68,68,0.08)",
                    border: "1px solid rgba(239,68,68,0.25)",
                    color: "var(--error, #ef4444)",
                  }}
                >
                  {flow.message}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Primary action: email input + continue button */}
            <form onSubmit={handleEmailSubmit} className="space-y-4 mb-6">
              <div className="space-y-1.5">
                <label
                  htmlFor="email"
                  className="text-sm font-medium lowercase"
                  style={{ color: "var(--foreground-muted)" }}
                >
                  {t("workEmail")}
                </label>
                <input
                  id="email"
                  type="email"
                  className="input w-full"
                  placeholder={t("emailPlaceholder")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  autoFocus
                  disabled={isBusy}
                />
              </div>

              <button
                type="submit"
                className="btn-primary w-full"
                disabled={isBusy || !email.trim()}
              >
                {getContinueLabel()}
              </button>
            </form>

            {/* Divider */}
            <div className="relative flex items-center gap-4 mb-6">
              <div
                className="flex-1 h-px"
                style={{ background: "var(--border)" }}
              />
              <span
                className="text-xs lowercase"
                style={{ color: "var(--foreground-muted)" }}
              >
                {t("orContinueWith")}
              </span>
              <div
                className="flex-1 h-px"
                style={{ background: "var(--border)" }}
              />
            </div>

            {/* Secondary actions: social provider buttons */}
            <div className="space-y-3">
              <button
                type="button"
                onClick={handleGoogle}
                className="btn-secondary w-full flex items-center justify-center gap-3"
              >
                <svg
                  viewBox="0 0 24 24"
                  className="w-4 h-4 flex-shrink-0"
                  aria-hidden="true"
                >
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                {t("googleWorkspace")}
              </button>

              <button
                type="button"
                onClick={handleMicrosoft}
                className="btn-secondary w-full flex items-center justify-center gap-3"
              >
                <svg
                  viewBox="0 0 24 24"
                  className="w-4 h-4 flex-shrink-0"
                  aria-hidden="true"
                >
                  <path fill="#F25022" d="M1 1h10.5v10.5H1z" />
                  <path fill="#00A4EF" d="M12.5 1H23v10.5H12.5z" />
                  <path fill="#7FBA00" d="M1 12.5h10.5V23H1z" />
                  <path fill="#FFB900" d="M12.5 12.5H23V23H12.5z" />
                </svg>
                {t("microsoft")}
              </button>
            </div>

            {/* Footer link */}
            <p
              className="mt-8 text-sm text-center"
              style={{ color: "var(--foreground-muted)" }}
            >
              {tAuth("noAccount")}{" "}
              <Link
                href="/signup"
                className="font-medium transition-colors"
                style={{ color: "var(--alana-coral)" }}
              >
                {tAuth("signup")}
              </Link>
            </p>
          </motion.div>
        ) : (
          /* ---------------------------------------------------------------- */
          /* Code input step                                                  */
          /* ---------------------------------------------------------------- */
          <motion.div
            key="code-step"
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -16 }}
            transition={{ duration: 0.2 }}
          >
            <div className="space-y-2 mb-8">
              <h1
                className="text-3xl font-bold"
                style={{ fontFamily: "MuseoModerno, sans-serif" }}
              >
                {t("checkYourEmail")}
              </h1>
              <p
                className="text-sm"
                style={{ color: "var(--foreground-muted)" }}
              >
                {t("weSentCodeTo", { email: stepEmail })}
              </p>
            </div>

            {/* Verification error */}
            <AnimatePresence>
              {flow.step === "error" && flow.email && (
                <motion.div
                  key="verify-error"
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.2 }}
                  className="mb-6 px-4 py-3 rounded-xl text-sm"
                  style={{
                    background: "rgba(239,68,68,0.08)",
                    border: "1px solid rgba(239,68,68,0.25)",
                    color: "var(--error, #ef4444)",
                  }}
                >
                  {flow.message}
                </motion.div>
              )}
            </AnimatePresence>

            {/* 6-digit code input */}
            <OtpInput
              onComplete={(code) => handleVerify(code, stepEmail)}
              isLoading={flow.step === "verifying"}
              key={flow.step === "error" ? "reset" : "active"}
            />

            {/* Resend / back links */}
            <div className="mt-6 flex flex-col gap-3 items-center">
              <button
                type="button"
                onClick={handleResend}
                disabled={resendCooldown > 0}
                className="text-sm transition-colors"
                style={{
                  color:
                    resendCooldown > 0
                      ? "var(--foreground-muted)"
                      : "var(--alana-coral)",
                  cursor: resendCooldown > 0 ? "not-allowed" : "pointer",
                }}
              >
                {resendCooldown > 0
                  ? t("resendCodeIn", { seconds: resendCooldown })
                  : t("resendCode")}
              </button>

              <button
                type="button"
                onClick={handleBack}
                className="text-sm transition-colors"
                style={{ color: "var(--foreground-muted)" }}
              >
                {t("useDifferentEmail")}
              </button>
            </div>

            {/* Footer link */}
            <p
              className="mt-8 text-sm text-center"
              style={{ color: "var(--foreground-muted)" }}
            >
              {tAuth("noAccount")}{" "}
              <Link
                href="/signup"
                className="font-medium transition-colors"
                style={{ color: "var(--alana-coral)" }}
              >
                {tAuth("signup")}
              </Link>
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

// ---------------------------------------------------------------------------
// OTP Input component — 6 individual digit boxes
// ---------------------------------------------------------------------------

interface OtpInputProps {
  onComplete: (code: string) => void;
  isLoading: boolean;
}

function OtpInput({ onComplete, isLoading }: OtpInputProps) {
  const t = useTranslations("auth.loginPage");
  const [digits, setDigits] = useState<string[]>(Array(6).fill(""));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const submittedRef = useRef(false);

  // Auto-focus first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  // Reset the guard whenever the user changes a digit (new attempt)
  useEffect(() => {
    submittedRef.current = false;
  }, [digits]);

  // Trigger submission when all 6 digits are filled
  useEffect(() => {
    if (isLoading) return;
    if (submittedRef.current) return;
    const code = digits.join("");
    if (code.length === 6 && /^\d{6}$/.test(code)) {
      submittedRef.current = true;
      onComplete(code);
    }
  }, [digits, isLoading, onComplete]);

  function handleChange(index: number, value: string) {
    // Strip non-digits
    const cleaned = value.replace(/\D/g, "");
    if (!cleaned) return;

    // Handle paste of full code into any input
    if (cleaned.length > 1) {
      const chars = cleaned.slice(0, 6).split("");
      const next = [...digits];
      chars.forEach((ch, i) => {
        if (index + i < 6) next[index + i] = ch;
      });
      setDigits(next);
      // Focus the input after the last pasted digit
      const focusIndex = Math.min(index + chars.length, 5);
      inputRefs.current[focusIndex]?.focus();
      return;
    }

    // Single digit — set and advance
    const next = [...digits];
    next[index] = cleaned;
    setDigits(next);
    if (index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  }

  function handleKeyDown(
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) {
    if (e.key === "Backspace") {
      e.preventDefault();
      if (digits[index]) {
        // Clear current cell
        const next = [...digits];
        next[index] = "";
        setDigits(next);
      } else if (index > 0) {
        // Move to previous cell and clear it
        const next = [...digits];
        next[index - 1] = "";
        setDigits(next);
        inputRefs.current[index - 1]?.focus();
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  }

  function handlePaste(e: React.ClipboardEvent) {
    e.preventDefault();
    const text = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!text) return;
    const chars = text.split("");
    const next = Array(6).fill("");
    chars.forEach((ch, i) => (next[i] = ch));
    setDigits(next);
    const focusIndex = Math.min(chars.length, 5);
    inputRefs.current[focusIndex]?.focus();
  }

  return (
    <div
      className="flex gap-2 justify-center"
      role="group"
      aria-label={t("verificationCode")}
    >
      {digits.map((digit, i) => (
        <input
          key={i}
          ref={(el) => {
            inputRefs.current[i] = el;
          }}
          type="text"
          inputMode="numeric"
          pattern="\d*"
          maxLength={6}
          value={digit}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={handlePaste}
          disabled={isLoading}
          aria-label={t("digit", { number: i + 1 })}
          className="w-11 h-14 text-center text-xl font-semibold rounded-xl border transition-all outline-none"
          style={{
            background: "var(--surface)",
            borderColor: digit ? "var(--alana-coral)" : "var(--border)",
            color: "var(--foreground)",
            caretColor: "var(--alana-coral)",
            boxShadow: digit ? "0 0 0 1px var(--alana-coral)" : undefined,
            opacity: isLoading ? 0.6 : 1,
          }}
        />
      ))}
    </div>
  );
}
